import { useRef } from 'react';
import PropTypes from 'prop-types';
import { Utils } from 'mw-style-react';

import { GRAPH_NODE_SIZE } from '@control-front-end/common/constants/graphActors';
import AppUtils from '@control-front-end/utils/utils';
import { AVERAGE_CHAR_WIDTH_FACTOR } from 'constants';

const PLUS_BUTTON_SIZE = 16;
const MOVEMENT_THRESHOLD = 5; // pixels

/**
 * Poppers for the graph
 */
function GraphPopper(props) {
  const {
    graph,
    graphContainer,
    graphEdgeHandles,
    edgeReconnect,
    ehStarted,
    readOnly,
    isSingleLayerModel,
    handleFlowchartActorsPopup,
  } = props;

  const currentMarkupStateIdRef = useRef(null);
  // Refs for tracking dragging state across all poppers
  const isDraggingRef = useRef(false);
  const mouseStartPosRef = useRef(null);

  const addPopper = (params) => {
    const {
      graphElement,
      popper,
      style,
      title,
      tooltipTitle,
      renderedPosition,
      placement,
      modifiers: customModifiers,
      offset,
    } = params;
    const { className, backgroundColor, totalSize, fontSize, scalable, width } =
      style;
    popper.div = document.createElement('div');
    popper.div.classList.add(className);
    popper.div.style.backgroundColor = backgroundColor;
    const sourceGrabbable = graphElement.source()
      ? graphElement.source().grabbable()
      : false;
    const layerSettings = graphElement.data('layerSettings');
    const isPinned = layerSettings && layerSettings.pin;
    popper.div.style.display =
      graphElement.grabbable() ||
      sourceGrabbable ||
      graphElement?.data('isClone') ||
      (className === 'popper-handle' && isPinned) ||
      className === 'popper-linked-actor'
        ? 'block'
        : 'none';
    const currentScale = graph.current.zoom();
    const popperSize = totalSize * currentScale;
    if (totalSize) {
      popper.div.style.width = `${popperSize}px`;
      popper.div.style.height = `${popperSize}px`;
      popper.div.style.backgroundSize = `${popperSize}px`;
    }
    if (fontSize) popper.div.style.fontSize = `${fontSize * currentScale}px`;
    popper.format = { totalSize, fontSize, scalable };
    if (title) {
      const label = document.createElement('span');
      label.innerHTML = title.length <= 30 ? title : `${title.slice(0, 30)}...`;
      label.style.position = 'absolute';
      label.style.left = '30px';
      popper.div.appendChild(label);
    }
    if (tooltipTitle) {
      const label = document.createElement('span');
      label.innerHTML = tooltipTitle;
      popper.div.style.display = 'flex';
      popper.div.style.width = `${width || 0}px`;
      popper.div.appendChild(label);
    }

    // Make popper-handle larger for better interaction area
    if (className === 'popper-handle') {
      // Make the button 1.5x larger but background stays original size
      const buttonSize = popperSize * 1.5;
      popper.div.style.width = `${buttonSize}px`;
      popper.div.style.height = `${buttonSize}px`;
      popper.div.style.backgroundSize = `${popperSize}px`;
    }

    graphContainer.current.appendChild(popper.div);
    const popperConfig = {
      content: popper.div,
      popper: {
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: offset || [
                0,
                -(className === 'popper-handle'
                  ? popperSize * 1.5
                  : popperSize || 0) / 2,
              ],
            },
          },
        ],
      },
    };
    if (renderedPosition) popperConfig.renderedPosition = renderedPosition;
    if (placement) popperConfig.popper.placement = placement;
    if (customModifiers) popperConfig.popper.modifiers.push(...customModifiers);
    popper.obj = graphElement.popper(popperConfig);
  };

  const updatePopper = (popper) => {
    if (popper.obj) popper.obj.update();
    if (!popper.div || !popper.format || !popper.format.scalable) return;
    const { totalSize, fontSize } = popper.format;
    const currentScale = graph.current.zoom();
    const popperSize = totalSize * currentScale;

    // Check if this is an enlarged popper-handle
    if (popper.div.classList.contains('popper-handle')) {
      const buttonSize = popperSize * 1.5;
      popper.div.style.width = `${buttonSize}px`;
      popper.div.style.height = `${buttonSize}px`;
      popper.div.style.backgroundSize = `${popperSize}px`;
    } else {
      popper.div.style.width = `${popperSize}px`;
      popper.div.style.height = `${popperSize}px`;
      popper.div.style.backgroundSize = `${popperSize}px`;
    }

    if (fontSize) popper.div.style.fontSize = `${fontSize * currentScale}px`;
  };

  const removePopper = (popper) => {
    if (popper.obj) {
      popper.obj = null;
    }
    popper.clicked = false;
    if (popper.div) {
      // Remove specific event listeners if we have references
      if (popper.mouseDownHandler) {
        // Remove from popper div
        popper.div.removeEventListener('mousedown', popper.mouseDownHandler);
        popper.mouseDownHandler = null;
      }
      if (popper.mouseUpHandler) {
        document.removeEventListener('mouseup', popper.mouseUpHandler);
        popper.div.removeEventListener('mouseup', popper.mouseUpHandler);
        popper.mouseUpHandler = null;
      }
      if (popper.mouseMoveHandler) {
        document.removeEventListener('mousemove', popper.mouseMoveHandler);
        popper.mouseMoveHandler = null;
      }
      if (popper.mouseEnterHandler) {
        popper.div.removeEventListener('mouseenter', popper.mouseEnterHandler);
        popper.mouseEnterHandler = null;
      }
      if (popper.mouseLeaveHandler) {
        popper.div.removeEventListener('mouseleave', popper.mouseLeaveHandler);
        popper.mouseLeaveHandler = null;
      }
      // remove remaining event listeners
      const clonedElement = popper.div.cloneNode(true);
      popper.div.replaceWith(clonedElement);
      graphContainer.current.removeChild(clonedElement);
      popper.div = null;
    }
    // Clear node reference
    popper.nodeId = null;
  };

  /**
   * Display linked actor for connection
   */
  const edgeLinkedActorPopper = (edge) => {
    if (!edge) return;
    const popper = {};

    const addHandle = () => {
      if (popper.div) return;
      if (edge.removed()) return;
      const popperActor = edge.data('linkedActor');
      if (!popperActor) return;
      addPopper({
        graphElement: edge,
        popper,
        style: {
          className: 'popper-linked-actor',
          backgroundColor: popperActor.color || 'white',
          totalSize: 16,
          fontSize: 12,
          scalable: true,
        },
        title: popperActor.title || '',
        placement: 'bottom',
      });
    };

    const updateHandle = () => {
      updatePopper(popper);
    };

    const removeHandle = () => {
      currentMarkupStateIdRef.current = null;
      removePopper(popper);
    };

    const changeStyleHandle = () => {
      if (edge.classes().includes('hide')) {
        removeHandle();
        return;
      }
      addHandle();
      if (!popper.div) return;
      if (edge.classes().includes('faded')) {
        popper.div.classList.add('faded');
      } else {
        popper.div.classList.remove('faded');
      }
    };

    const coordIsNaN = ({ x, y }) => {
      return Number.isNaN(x) || Number.isNaN(y);
    };

    const moveNodesHandle = () => {
      if (!edge) return;
      if (edge.removed() || coordIsNaN(edge.midpoint())) {
        removeHandle();
        return;
      }
      addHandle();
    };

    if (edge.data('linkedActorId')) {
      addHandle();
    }
    graph.current.on('pan zoom resize render', updateHandle);
    edge.on('data', () => {
      if (!edge.data('linkedActorId')) {
        removeHandle();
        return;
      }
      addHandle();
    });
    edge.on('style', changeStyleHandle);
    edge.on('remove', removeHandle);
    const sourceNode = edge.source();
    const targetNode = edge.target();
    sourceNode.on('position', moveNodesHandle);
    targetNode.on('position', moveNodesHandle);
  };

  /**
   * Hook for new connection
   */
  const edgeHandlesPopper = () => {
    let currentPopper = null;
    let currentNodeId = null;
    let currentNode = null;
    let currentMouseOutHandler = null;
    let currentMouseEnterHandler = null;
    let mouseOutTimeout = null;
    let isOnCurrentNode = false;

    const removeHandle = () => {
      // Clear any pending timeout
      if (mouseOutTimeout) {
        clearTimeout(mouseOutTimeout);
        mouseOutTimeout = null;
      }

      if (currentPopper) {
        removePopper(currentPopper);
        currentPopper = null;
        currentNodeId = null;
        isOnCurrentNode = false;
      }
      // Remove mouseout and mouseenter handlers from current node
      if (currentNode) {
        if (currentMouseOutHandler) {
          currentNode.off('mouseout', currentMouseOutHandler);
          currentMouseOutHandler = null;
        }
        if (currentMouseEnterHandler) {
          currentNode.off('mouseover', currentMouseEnterHandler);
          currentMouseEnterHandler = null;
        }
        currentNode = null;
      }
    };

    const removeAllHandles = () => {
      removeHandle();
    };

    const setHandleOn = (node) => {
      if (ehStarted.current) return;
      const nodeData = node.data();
      const {
        isLayer,
        isAutoLayerNode,
        accessDenied,
        readOnly: readOnlyNode,
        isNonInteractive,
        isStateMarkup,
        isClone,
      } = nodeData;
      if (
        accessDenied ||
        readOnlyNode ||
        readOnly ||
        isLayer ||
        isAutoLayerNode ||
        isNonInteractive ||
        isStateMarkup
      ) {
        return;
      }

      const nodeId = node.id();
      // Check if popper already exists for this node to prevent duplicate creation
      if (currentNodeId === nodeId && currentPopper) return;

      // Remove any existing popper before creating a new one
      removeHandle();

      // Create a new popper for this node
      const popper = {};
      currentPopper = popper;
      currentNodeId = nodeId;

      const offset = isClone
        ? [0, -PLUS_BUTTON_SIZE * (graph?.current?.zoom?.() || 1)]
        : undefined;

      addPopper({
        graphElement: node,
        popper,
        style: {
          className: 'popper-handle',
          totalSize: PLUS_BUTTON_SIZE,
        },
        placement: 'bottom',
        offset,
      });

      // Ensure event listener is attached after DOM element is created
      if (popper.div) {
        // analyze cursor position change for graphEdgeHandles start
        const createHandlers = () => {
          const handlers = {};
          handlers.handleMouseUp = (e) => {
            // Clean up global listeners
            document.removeEventListener('mousemove', handlers.handleMouseMove);
            document.removeEventListener('mouseup', handlers.handleMouseUp);

            // Only handle popup case - edge creation during drag is handled in handleMouseMove
            if (!isDraggingRef.current && node.data('isFlowchart')) {
              if (handleFlowchartActorsPopup) {
                handleFlowchartActorsPopup({
                  node,
                  renderedPosition: { x: e.clientX, y: e.clientY },
                });
              } else {
                // Fallback to edge creation if no popup handler
                graphEdgeHandles.current.start(node);
              }
            }

            // Reset state
            isDraggingRef.current = false;
            mouseStartPosRef.current = null;
          };

          handlers.handleMouseMove = (e) => {
            if (mouseStartPosRef.current && !isDraggingRef.current) {
              const deltaX = Math.abs(e.clientX - mouseStartPosRef.current.x);
              const deltaY = Math.abs(e.clientY - mouseStartPosRef.current.y);

              const currentZoom = graph.current?.zoom?.() || 1;
              const adjustedThreshold = MOVEMENT_THRESHOLD * currentZoom;
              if (deltaX > adjustedThreshold || deltaY > adjustedThreshold) {
                isDraggingRef.current = true;
                // Clean up listeners immediately and start edge creation
                document.removeEventListener(
                  'mousemove',
                  handlers.handleMouseMove
                );
                document.removeEventListener('mouseup', handlers.handleMouseUp);
                graphEdgeHandles.current.start(node);
              }
            }
          };

          return handlers;
        };

        const { handleMouseMove, handleMouseUp } = createHandlers();

        const handleMouseDown = (e) => {
          e.preventDefault();
          e.stopPropagation();

          // Reset and initialize dragging state
          isDraggingRef.current = false;
          mouseStartPosRef.current = { x: e.clientX, y: e.clientY };
          document.addEventListener('mousemove', handleMouseMove);
          document.addEventListener('mouseup', handleMouseUp);
        };

        // Add event listener to the enlarged button
        popper.div.addEventListener('mousedown', handleMouseDown, {
          capture: true,
        });

        // Store references to the handlers for cleanup
        popper.mouseDownHandler = handleMouseDown;
        popper.mouseUpHandler = handleMouseUp;
        popper.mouseMoveHandler = handleMouseMove;
      }

      // Use a timeout to prevent immediate removal on mouseout
      const mouseOutHandler = (event) => {
        // Check if we're moving to the popper element
        const relatedTarget =
          event.originalEvent?.target || event.originalEvent?.relatedTarget;

        if (
          popper?.div?.contains(relatedTarget) ||
          relatedTarget === popper.div
        ) {
          // Mouse moved to popper or overlay, don't remove
          isOnCurrentNode = false;
          return;
        }

        mouseOutTimeout = setTimeout(() => {
          // Only remove if this is still the current node
          if (currentNodeId === nodeId) {
            removeHandle();
          }
        }, 100); // Small delay to prevent premature removal
      };

      // Store references for cleanup
      currentNode = node;
      currentNodeId = nodeId;
      isOnCurrentNode = true;
      currentMouseOutHandler = mouseOutHandler;
      node.on('mouseout', mouseOutHandler);

      // Add mouseover handler to node to mark that we're back on current node
      const mouseEnterHandler = () => {
        isOnCurrentNode = true;
      };
      currentMouseEnterHandler = mouseEnterHandler;
      node.on('mouseover', mouseEnterHandler);

      // Cancel the timeout if mouse enters the popper
      if (popper.div) {
        popper.div.addEventListener('mouseenter', () => {
          if (mouseOutTimeout) {
            clearTimeout(mouseOutTimeout);
            mouseOutTimeout = null;
          }
        });

        // Also handle mouseleave from popper
        popper.div.addEventListener('mouseleave', () => {
          mouseOutTimeout = setTimeout(() => {
            // Only remove if we're still on popper (not returned to node)
            if (!isOnCurrentNode && currentNodeId === nodeId) {
              removeHandle();
            }
          }, 100);
        });
      }
    };

    const handleMouseOver = (event) => {
      const selectedEdges = graph.current.elements('edge:selected');
      if (selectedEdges.length || currentNodeId === event.target.id()) return;
      setHandleOn(event.target);
    };

    graph.current.on('mouseover', 'node', handleMouseOver);
    graph.current.on('zoom pan', removeAllHandles);
    graph.current.on('grab', 'node', removeAllHandles);
    graph.current.on('tap', (e) => {
      if (e.target === graph.current) removeAllHandles();
    });
    graph.current.on('ehstart', () => {
      ehStarted.current = true;
    });
    graph.current.on('ehstop', () => {
      ehStarted.current = false;
    });
  };

  /**
   * Hook at the end of connection for changing source/target
   */
  const edgeReconnectPopper = (edge, side) => {
    const popper = {};
    const edgeSideNode = edge[side]();
    const anotherSide = side === 'source' ? 'target' : 'source';
    const getPosFunc = `rendered${Utils.capitalize(anotherSide)}Endpoint`;
    const targetSideNode = edge[anotherSide]();
    if (
      edgeSideNode.data('isLayer') ||
      edgeSideNode.data('isAutoLayerRoot') ||
      targetSideNode.data('isAutoLayerRoot')
    )
      return;

    const updateHandle = () => {
      return updatePopper(popper);
    };

    const removeHandle = () => {
      return removePopper(popper);
    };

    const handleMouseDown = (e) => {
      e.stopPropagation();
      edge.addClass('hide');
      removeHandle();
      graphEdgeHandles.current.start(edgeSideNode);
      edgeReconnect.current = { id: edge.id(), side: anotherSide };
    };

    const addHandle = () => {
      addPopper({
        graphElement: edge,
        popper,
        style: {
          className: 'popper',
        },
        renderedPosition: () => edge[getPosFunc](),
        offset: [-2, -6],
      });
      popper.div.addEventListener('mousedown', handleMouseDown);
    };

    const handleEhPreview = () => {
      const eh = graph.current.$('.eh-ghost-edge, .eh-preview');
      // set arrow for connection preview depending on its direction
      const ehPreviewClass =
        edgeReconnect.current && edgeReconnect.current.side === 'source'
          ? 'eh-reverse-arrow'
          : 'eh-arrow';
      eh.forEach((i) => i.addClass(ehPreviewClass));
    };

    addHandle();
    graph.current.on('pan zoom resize', updateHandle);
    graph.current.on('ehpreviewon ehhoverover', handleEhPreview);
    edge.on('unselect remove', removeHandle);
    edgeSideNode.on('position', () => edge.unselect());
  };

  const edgeAddTransferPopper = (handleClick) => {
    const popper = {};

    const removeHandle = () => {
      handleClick(null);
      graph.current.elements('edge:selected').unselect();
      return removePopper(popper);
    };

    const setHandleOn = (edge) => {
      if (
        isSingleLayerModel ||
        edge.data('isNonInteractive') ||
        edge.data('isTree')
      ) {
        return;
      }
      addPopper({
        graphElement: edge,
        popper,
        style: {
          className: 'popper-handle',
          totalSize: PLUS_BUTTON_SIZE,
        },
        placement: 'bottom',
      });
      if (popper.div && handleClick) {
        popper.div.addEventListener('mousedown', () => {
          popper.clicked = true;
          handleClick({ edge, popperDiv: popper.div, removeHandle });
        });
      }
      edge.on('mouseout', () => {
        if (!popper.clicked) removeHandle();
      });
    };

    const handleMouseOver = (event) => {
      const selectedEdges = graph.current.elements('edge:selected');
      if (selectedEdges.length) return;
      setHandleOn(event.target);
    };

    graph.current.on('mouseover', 'edge', handleMouseOver);
    graph.current.on('zoom pan', removeHandle);
    graph.current.on('grab', 'node', removeHandle);
    graph.current.on('tap', (e) => {
      if (e.target === graph.current) {
        removeHandle();
        if (handleClick) handleClick(null);
      }
    });
  };

  const stateNodePopper = (onClick) => {
    const statePopper = {};

    const removeHandle = () => {
      removePopper(statePopper);
    };

    const setHandleOn = (node) => {
      const { x1, y1, x2, y2 } = graph.current.extent();
      const polygon = node.data('polygon');
      if (!polygon) return;
      const { x2: x, y2: y } = AppUtils.makeBoundingBoxFromPolygon(polygon);
      // if state right-bottom corner is outside - don't show the popper
      // otherwise cytoscape-popper render it in unexpected position with ui bugs
      const popperPlaceInViewport = x > x1 && x < x2 && y > y1 && y < y2;
      if (!popperPlaceInViewport) return;
      const zoom = graph.current.zoom();
      const radius = GRAPH_NODE_SIZE / 2;
      const padding = 8;
      const halfWidth = node.renderedBoundingbox().w / 2;
      addPopper({
        graphElement: node,
        popper: statePopper,
        style: {
          className: 'popper-handle',
          totalSize: radius,
        },
        placement: 'bottom',
        offset: [
          halfWidth - (radius - padding) * zoom,
          -(radius + padding) * zoom,
        ],
      });

      if (statePopper.div && onClick) {
        statePopper.div.addEventListener('mousedown', (e) => {
          e.stopPropagation();
          if (statePopper.clicked) {
            statePopper.clicked = false;
            onClick(null);
          } else {
            statePopper.clicked = true;
            onClick({
              stateActor: node.data(),
              popperDiv: statePopper.div,
              position: node.position(),
              renderedPosition: { x: e.clientX, y: e.clientY },
            });
          }
        });
      }

      node.on('mouseout grab', ({ type, position }) => {
        if (!position) return;
        const { x, y } = position;
        const { x1, x2, y1, y2 } = node.boundingBox();
        const mouseInState = x > x1 && x < x2 && y > y1 && y < y2;
        if (type === 'mouseout' && mouseInState) return;
        removeHandle();
      });
      graph.current.on('pan zoom resize render', removeHandle);
    };

    graph.current.on('mousemove', 'node[?isStateMarkup][!isTrace]', (e) => {
      if (
        !statePopper.obj ||
        currentMarkupStateIdRef.current !== e.target.id()
      ) {
        removeHandle();
        currentMarkupStateIdRef.current = e.target.id();
        setHandleOn(e.target);
      }
    });
  };

  const nodeTitleTooltipPopper = () => {
    const popper = {};

    const removeHandle = () => {
      return removePopper(popper);
    };

    const showTooltip = (node) => {
      const { title, tooltip } = node.data();
      const fontSize = 14;
      const tooltipTitle = tooltip || title || '';
      const approximateWidth =
        tooltipTitle.length * fontSize * AVERAGE_CHAR_WIDTH_FACTOR;
      addPopper({
        graphElement: node,
        popper,
        style: {
          className: 'popper-tooltip',
          width: Math.min(approximateWidth, 360),
        },
        placement: 'top',
        tooltipTitle,
        offset: [0, 6],
        modifiers: [
          {
            name: 'flip',
            enabled: true,
            options: {
              fallbackPlacements: [
                'top',
                'top-start',
                'top-end',
                'bottom',
                'bottom-start',
                'bottom-end',
                'left',
                'left-start',
                'left-end',
                'right',
                'right-start',
                'right-end',
              ], // Flip to any of these positions if needed
              padding: {
                top: 90,
                bottom: 40,
                left: 45,
              },
            },
          },
        ],
      });
      node.on('mouseout', removeHandle);
    };

    graph.current.on('mouseover', 'node', (event) => {
      showTooltip(event.target);
    });
    graph.current.on('zoom pan', removeHandle);
    graph.current.on('grab', 'node', removeHandle);
  };

  return {
    edgeLinkedActorPopper,
    edgeReconnectPopper,
    edgeHandlesPopper,
    edgeAddTransferPopper,
    stateNodePopper,
    nodeTitleTooltipPopper,
  };
}

GraphPopper.propTypes = {
  graph: PropTypes.object,
  graphContainer: PropTypes.object,
  graphEdgeHandles: PropTypes.object,
  edgeReconnect: PropTypes.object,
  ehStarted: PropTypes.object,
  readOnly: PropTypes.bool,
  isSingleLayerModel: PropTypes.bool,
  handleFlowchartActorsPopup: PropTypes.func,
};

export default GraphPopper;
